import React from 'react';
import { Link } from 'react-router-dom';
import { FaHome, FaChevronRight, FaServer, FaNetworkWired, FaShieldAlt, FaCogs, FaCheckCircle, FaArrowRight, FaPhone, FaCloud, FaDatabase } from 'react-icons/fa';
import Navigation from '../components/Navigation';
import TopBar from '../components/TopBar';
import Footer from '../components/Footer';

const InfrastructurePage = () => {
    const features = [
        {
            icon: <FaServer size={24} />,
            title: 'Server Infrastructure',
            description: 'Enterprise-grade server solutions with high availability and redundancy for mission-critical operations.',
            gradient: 'from-blue-600 to-indigo-600'
        },
        {
            icon: <FaNetworkWired size={24} />,
            title: 'Network Architecture',
            description: 'Scalable network designs that grow with your business while maintaining optimal performance.',
            gradient: 'from-cyan-500 to-blue-500'
        },
        {
            icon: <FaCloud size={24} />,
            title: 'Cloud Integration',
            description: 'Seamless hybrid cloud solutions connecting on-premise infrastructure with cloud services.',
            gradient: 'from-purple-600 to-indigo-600'
        },
        {
            icon: <FaDatabase size={24} />,
            title: 'Storage Solutions',
            description: 'High-performance storage systems with data protection and disaster recovery capabilities.',
            gradient: 'from-teal-500 to-cyan-500'
        },
        {
            icon: <FaShieldAlt size={24} />,
            title: 'Security Hardening',
            description: 'Infrastructure security measures including firewalls, intrusion detection, and encryption.',
            gradient: 'from-red-500 to-rose-500'
        },
        {
            icon: <FaCogs size={24} />,
            title: 'System Optimization',
            description: 'Performance tuning and optimization to maximize your infrastructure ROI.',
            gradient: 'from-orange-500 to-amber-500'
        }
    ];

    const benefits = [
        '99.9% uptime guarantee',
        '24/7 monitoring and support',
        'Scalable architecture',
        'Cost-effective solutions',
        'Expert implementation',
        'Proactive maintenance'
    ];

    return (
        <div className="font-sans">
            <TopBar />
            <Navigation />

            {/* Hero Section */}
            <section className="relative bg-gradient-to-br from-gray-900 via-blue-900 to-indigo-900 py-24 overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute inset-0" style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                    }}></div>
                </div>

                <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500 rounded-full filter blur-3xl opacity-20 translate-x-1/2 -translate-y-1/2"></div>
                <div className="absolute bottom-0 left-0 w-96 h-96 bg-indigo-500 rounded-full filter blur-3xl opacity-20 -translate-x-1/2 translate-y-1/2"></div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <nav className="flex items-center gap-2 text-sm text-gray-300 mb-8">
                        <Link to="/" className="flex items-center gap-1 hover:text-white transition-colors">
                            <FaHome size={14} />
                            <span>Home</span>
                        </Link>
                        <FaChevronRight size={10} className="text-gray-500" />
                        <span className="text-blue-400">Infrastructure Solutions</span>
                    </nav>

                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                                Enterprise <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">Infrastructure</span> Solutions
                            </h1>
                            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                                Build a rock-solid foundation for your business with our comprehensive infrastructure solutions designed for performance, reliability, and scalability.
                            </p>
                            <div className="flex flex-wrap gap-4">
                                <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-blue-500/30 transition-all duration-300">
                                    Get Started
                                    <FaArrowRight size={14} />
                                </Link>
                                <a href="tel:+12014099845" className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white/30 text-white rounded-xl font-semibold hover:bg-white/10 transition-all duration-300">
                                    <FaPhone size={14} />
                                    Call Us Now
                                </a>
                            </div>
                        </div>
                        <div className="relative">
                            <div className="bg-gradient-to-br from-blue-600/20 to-indigo-600/20 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
                                <img
                                    src="https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=600&h=400&fit=crop"
                                    alt="Server Infrastructure"
                                    className="w-full h-80 object-cover rounded-xl shadow-2xl"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-4">Our Capabilities</span>
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Comprehensive Infrastructure Services</h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            End-to-end infrastructure solutions tailored to meet your business requirements
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {features.map((feature, index) => (
                            <div
                                key={index}
                                className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-blue-200"
                            >
                                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform duration-300`}>
                                    {feature.icon}
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Benefits Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-blue-50">
                <div className="max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        <div>
                            <img
                                src="https://images.unsplash.com/photo-1551434678-e076c223a692?w=600&h=500&fit=crop"
                                alt="IT Team Working"
                                className="w-full h-auto rounded-2xl shadow-2xl"
                            />
                        </div>
                        <div>
                            <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-4">Why Choose Us</span>
                            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Benefits of Our Infrastructure Solutions</h2>
                            <p className="text-lg text-gray-600 mb-8">
                                Partner with us to transform your IT infrastructure into a competitive advantage. Our solutions are designed to deliver measurable results.
                            </p>
                            <div className="grid grid-cols-2 gap-4">
                                {benefits.map((benefit, index) => (
                                    <div key={index} className="flex items-center gap-3">
                                        <FaCheckCircle className="text-blue-600 flex-shrink-0" size={20} />
                                        <span className="text-gray-700 font-medium">{benefit}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-600 to-indigo-600">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Ready to Transform Your Infrastructure?</h2>
                    <p className="text-xl text-blue-100 mb-8">
                        Let our experts design a custom infrastructure solution that drives your business forward.
                    </p>
                    <div className="flex flex-wrap justify-center gap-4">
                        <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 bg-white text-blue-600 rounded-xl font-semibold hover:shadow-lg transition-all duration-300">
                            Request a Consultation
                            <FaArrowRight size={14} />
                        </Link>
                        <a href="tel:+12014099845" className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white text-white rounded-xl font-semibold hover:bg-white/10 transition-all duration-300">
                            <FaPhone size={14} />
                            Speak to an Expert
                        </a>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default InfrastructurePage;
